import React, { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Bell,
  BellOff,
  Check,
  CheckCheck,
  Trash2,
  X,
  Settings,
  Info,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Calendar,
  CalendarDays,
  Shield,
  Loader2,
  ChevronDown,
  Volume2,
  VolumeX,
  Smartphone,
  Download,
  Share2,
} from 'lucide-react';
import { useNotificationStore } from '../stores/notificationStore';
import { formatDistanceToNow } from 'date-fns';
import { vi } from 'date-fns/locale';

// Category icons
const categoryIcons = {
  attendance: Calendar,
  leave: CalendarDays,
  overtime: Clock,
  approval: CheckCircle,
  system: Shield,
};

// Type styling
const typeStyles = {
  info: { bg: 'bg-blue-50 dark:bg-blue-900/20', icon: Info, color: 'text-blue-500' },
  warning: { bg: 'bg-amber-50 dark:bg-amber-900/20', icon: AlertTriangle, color: 'text-amber-500' },
  success: { bg: 'bg-green-50 dark:bg-green-900/20', icon: CheckCircle, color: 'text-green-500' },
  error: { bg: 'bg-red-50 dark:bg-red-900/20', icon: XCircle, color: 'text-red-500' },
};

// Category labels
const categoryLabels = {
  attendance: 'Chấm công',
  leave: 'Nghỉ phép',
  overtime: 'Tăng ca',
  approval: 'Phê duyệt',
  system: 'Hệ thống',
};

function NotificationItem({ notification, onRead, onDelete, onNavigate }) {
  const style = typeStyles[notification.type] || typeStyles.info;
  const TypeIcon = style.icon;
  const CategoryIcon = categoryIcons[notification.category] || Shield;

  const timeAgo = notification.created_at
    ? formatDistanceToNow(new Date(notification.created_at), { addSuffix: true, locale: vi })
    : '';

  const handleClick = () => {
    if (!notification.is_read) {
      onRead(notification.notification_id);
    }
    if (notification.action_url) {
      onNavigate(notification.action_url);
    }
  };

  return (
    <div
      className={`group relative flex gap-3 p-3 border-b border-gray-100 dark:border-gray-700 cursor-pointer transition-all duration-200 hover:bg-gray-50 dark:hover:bg-gray-750 ${
        !notification.is_read ? style.bg : 'bg-white dark:bg-gray-800'
      }`}
      onClick={handleClick}
    >
      {/* Unread indicator */}
      {!notification.is_read && (
        <div className="absolute left-1 top-1/2 -translate-y-1/2 w-2 h-2 rounded-full bg-blue-500 animate-pulse" />
      )}

      {/* Icon */}
      <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center ${
        notification.is_read 
          ? 'bg-gray-100 dark:bg-gray-700' 
          : `${style.bg}`
      }`}>
        <TypeIcon className={`w-4.5 h-4.5 ${notification.is_read ? 'text-gray-400' : style.color}`} />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <p className={`text-sm font-medium leading-tight ${
            notification.is_read 
              ? 'text-gray-500 dark:text-gray-400' 
              : 'text-gray-900 dark:text-white'
          }`}>
            {notification.title}
          </p>
          {/* Actions */}
          <div className="flex-shrink-0 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            {!notification.is_read && (
              <button
                onClick={(e) => { e.stopPropagation(); onRead(notification.notification_id); }}
                className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400 hover:text-blue-500"
                title="Đánh dấu đã đọc"
              >
                <Check className="w-3.5 h-3.5" />
              </button>
            )}
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(notification.notification_id); }}
              className="p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-400 hover:text-red-500"
              title="Xoá"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
        <p className={`text-xs mt-0.5 leading-relaxed ${
          notification.is_read ? 'text-gray-400 dark:text-gray-500' : 'text-gray-600 dark:text-gray-300'
        }`}>
          {notification.message}
        </p>
        <div className="flex items-center gap-2 mt-1.5">
          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-gray-400 dark:text-gray-500">
            <CategoryIcon className="w-3 h-3" />
            {categoryLabels[notification.category] || notification.category}
          </span>
          <span className="text-[10px] text-gray-400 dark:text-gray-500">
            {timeAgo}
          </span>
          {notification.sender?.full_name && (
            <span className="text-[10px] text-gray-400 dark:text-gray-500">
              • {notification.sender.full_name}
            </span>
          )}
        </div>
      </div>
    </div>
  );
}

function NotificationSettings({ settings, onUpdate, onClose }) {
  const {
    pushPermission,
    requestPushPermission,
    disablePush,
    pwaCanInstall,
    pwaIsInstalled,
    installPWA,
    showIOSInstallHint,
    setIOSInstallHint,
  } = useNotificationStore();
  const [pushLoading, setPushLoading] = React.useState(false);
  const [installLoading, setInstallLoading] = React.useState(false);

  // Detect iOS for manual install instructions
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  const isInstalled = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;

  const handlePushToggle = async (val) => {
    setPushLoading(true);
    try {
      if (val) {
        await requestPushPermission();
        onUpdate({ push_enabled: true });
      } else {
        await disablePush();
      }
    } finally {
      setPushLoading(false);
    }
  };

  const handleInstall = async () => {
    if (isIOS) {
      setIOSInstallHint(true);
      return;
    }
    setInstallLoading(true);
    try {
      await installPWA();
    } finally {
      setInstallLoading(false);
    }
  };

  const categorySettings = [
    { key: 'category_attendance', label: 'Chấm công', icon: Calendar },
    { key: 'category_leave', label: 'Nghỉ phép', icon: CalendarDays },
    { key: 'category_overtime', label: 'Tăng ca (OT)', icon: Clock },
    { key: 'category_approval', label: 'Phê duyệt', icon: CheckCircle },
    { key: 'category_system', label: 'Hệ thống', icon: Shield },
  ];

  const reminderSettings = [
    { key: 'checkin_reminder', label: 'Nhắc check-in (trước 10 phút)', icon: Clock },
    { key: 'checkout_reminder', label: 'Nhắc check-out (quá giờ)', icon: AlertTriangle },
    { key: 'late_warning', label: 'Cảnh báo đi muộn', icon: AlertTriangle },
    { key: 'approval_reminder', label: 'Nhắc phê duyệt đang chờ', icon: CheckCircle },
  ];

  const locked = settings?.role_locked || {};

  return (
    <div className="p-4 overflow-y-auto max-h-[calc(100vh-8rem)]">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Cài đặt thông báo</h3>
        <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400">
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* PWA Install Banner */}
      {!isInstalled && (isIOS || pwaCanInstall) && (
        <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-700">
          <div className="flex items-start gap-2">
            <Smartphone className="w-4 h-4 text-blue-500 mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <p className="text-xs font-semibold text-blue-700 dark:text-blue-300">
                Thêm vào màn hình chính
              </p>
              <p className="text-[11px] text-blue-600 dark:text-blue-400 mt-0.5">
                {isIOS
                  ? 'Thêm app vào màn hình chính để nhận thông báo đẩy trên iPhone/iPad'
                  : 'Cài app để nhận thông báo kể cả khi không mở trình duyệt'}
              </p>
            </div>
            <button
              onClick={handleInstall}
              disabled={installLoading}
              className="flex-shrink-0 flex items-center gap-1 px-2.5 py-1 bg-blue-600 text-white text-[11px] font-medium rounded-md hover:bg-blue-700 transition-colors"
            >
              {installLoading ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : isIOS ? (
                <><Share2 className="w-3 h-3" /> Hướng dẫn</>
              ) : (
                <><Download className="w-3 h-3" /> Cài app</>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Installed status */}
      {isInstalled && (
        <div className="mb-4 p-2.5 bg-green-50 dark:bg-green-900/20 rounded-lg border border-green-200 dark:border-green-700 flex items-center gap-2">
          <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
          <p className="text-xs text-green-700 dark:text-green-300">App đã được cài vào màn hình chính</p>
        </div>
      )}

      {/* iOS Install Guide Modal */}
      {showIOSInstallHint && (
        <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-700">
          <div className="flex items-start justify-between gap-2 mb-2">
            <p className="text-xs font-semibold text-amber-700 dark:text-amber-300">
              Hướng dẫn thêm vào màn hình chính (iOS)
            </p>
            <button
              onClick={() => setIOSInstallHint(false)}
              className="p-0.5 rounded text-amber-500 hover:bg-amber-100 dark:hover:bg-amber-900"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
          <ol className="space-y-1.5 text-[11px] text-amber-700 dark:text-amber-400">
            <li className="flex items-start gap-1.5">
              <span className="flex-shrink-0 w-4 h-4 bg-amber-200 dark:bg-amber-800 rounded-full flex items-center justify-center text-[9px] font-bold">1</span>
              Nhấn nút <Share2 className="inline w-3 h-3 mx-0.5" /> <strong>Chia sẻ</strong> ở thanh trình duyệt Safari
            </li>
            <li className="flex items-start gap-1.5">
              <span className="flex-shrink-0 w-4 h-4 bg-amber-200 dark:bg-amber-800 rounded-full flex items-center justify-center text-[9px] font-bold">2</span>
              Cuộn xuống và chọn <strong>"Thêm vào Màn hình chính"</strong>
            </li>
            <li className="flex items-start gap-1.5">
              <span className="flex-shrink-0 w-4 h-4 bg-amber-200 dark:bg-amber-800 rounded-full flex items-center justify-center text-[9px] font-bold">3</span>
              Nhấn <strong>Thêm</strong> — sau đó mở app từ màn hình chính để bật thông báo
            </li>
          </ol>
          <p className="text-[10px] text-amber-500 mt-2">
            * Yêu cầu iOS 16.4+ và Safari. Sau khi thêm, mở app từ màn hình chính rồi bật thông báo đẩy.
          </p>
        </div>
      )}

      {/* Master toggle */}
      <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Bell className="w-4 h-4 text-gray-500" />
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Bật thông báo</span>
        </div>
        <ToggleSwitch
          checked={settings?.enabled !== false}
          onChange={(val) => onUpdate({ enabled: val })}
          disabled={locked.enabled}
        />
      </div>

      {/* Sound toggle */}
      <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2">
          {settings?.sound_enabled !== false ? (
            <Volume2 className="w-4 h-4 text-gray-500" />
          ) : (
            <VolumeX className="w-4 h-4 text-gray-500" />
          )}
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Âm thanh thông báo</span>
        </div>
        <ToggleSwitch
          checked={settings?.sound_enabled !== false}
          onChange={(val) => onUpdate({ sound_enabled: val })}
        />
      </div>

      {/* Push notification toggle */}
      <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <Smartphone className="w-4 h-4 text-gray-500" />
          <div>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Thông báo đẩy</span>
            {pushPermission === 'denied' && (
              <p className="text-[10px] text-red-500 mt-0.5">Đã bị chặn trong trình duyệt. Vào Settings → Notifications để bật lại.</p>
            )}
            {pushPermission === 'default' && (
              <p className="text-[10px] text-gray-400 mt-0.5">Chưa cấp quyền — nhấn toggle để bật</p>
            )}
            {pushPermission === 'granted' && (
              <p className="text-[10px] text-green-500 mt-0.5">Đang hoạt động</p>
            )}
            {pushPermission === 'unsupported' && (
              <p className="text-[10px] text-gray-400 mt-0.5">
                {isIOS && !isInstalled
                  ? 'Thêm vào màn hình chính trước, sau đó bật'
                  : 'Trình duyệt không hỗ trợ'}
              </p>
            )}
          </div>
        </div>
        {pushLoading ? (
          <Loader2 className="w-4 h-4 animate-spin text-primary-500" />
        ) : (
          <ToggleSwitch
            checked={settings?.push_enabled !== false && pushPermission === 'granted'}
            onChange={handlePushToggle}
            disabled={pushPermission === 'denied' || pushPermission === 'unsupported' || settings?.enabled === false}
          />
        )}
      </div>

      {/* Categories */}
      <div className="mt-3">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
          Danh mục thông báo
        </p>
        {categorySettings.map(({ key, label, icon: Icon }) => (
          <div key={key} className="flex items-center justify-between py-2.5">
            <div className="flex items-center gap-2">
              <Icon className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600 dark:text-gray-300">{label}</span>
              {locked[key] && (
                <span className="text-[9px] px-1.5 py-0.5 bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 rounded font-medium" title="Quản trị viên đã khoá, không thể tắt">
                  Bắt buộc
                </span>
              )}
            </div>
            <ToggleSwitch
              checked={settings?.[key] !== false}
              onChange={(val) => onUpdate({ [key]: val })}
              disabled={settings?.enabled === false || locked[key]}
            />
          </div>
        ))}
      </div>

      {/* Granular reminders */}
      <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
        <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
          Nhắc nhở chấm công
        </p>
        {reminderSettings.map(({ key, label, icon: Icon }) => (
          <div key={key} className="flex items-center justify-between py-2.5">
            <div className="flex items-center gap-2">
              <Icon className="w-4 h-4 text-gray-400" />
              <span className="text-sm text-gray-600 dark:text-gray-300">{label}</span>
              {locked[key] && (
                <span className="text-[9px] px-1.5 py-0.5 bg-orange-100 text-orange-600 dark:bg-orange-900/30 dark:text-orange-400 rounded font-medium" title="Quản trị viên đã khoá, không thể tắt">
                  Bắt buộc
                </span>
              )}
            </div>
            <ToggleSwitch
              checked={settings?.[key] !== false}
              onChange={(val) => onUpdate({ [key]: val })}
              disabled={settings?.enabled === false || settings?.category_attendance === false || locked[key]}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function ToggleSwitch({ checked, onChange, disabled = false }) {
  return (
    <button
      onClick={() => !disabled && onChange(!checked)}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
        disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
      } ${checked ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600'}`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white shadow-sm transition-transform duration-200 ${
          checked ? 'translate-x-[18px]' : 'translate-x-[3px]'
        }`}
      />
    </button>
  );
}

// Category filter tabs
const TABS = [
  { key: 'all', label: 'Tất cả' },
  { key: 'attendance', label: 'Chấm công' },
  { key: 'leave', label: 'Nghỉ phép' },
  { key: 'overtime', label: 'OT' },
  { key: 'approval', label: 'Phê duyệt' },
  { key: 'system', label: 'Hệ thống' },
];

export default function NotificationPanel() {
  const navigate = useNavigate();
  const panelRef = useRef(null);
  const [activeTab, setActiveTab] = React.useState('all');
  const [showSettings, setShowSettings] = React.useState(false);

  const {
    notifications,
    unreadCount,
    isLoading,
    isPanelOpen,
    settings,
    pagination,
    closePanel,
    fetchNotifications,
    fetchSettings,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllRead,
    deleteAll,
    updateSettings,
  } = useNotificationStore();

  // Fetch data when panel opens
  useEffect(() => {
    if (isPanelOpen) {
      fetchNotifications();
      fetchSettings();
    }
  }, [isPanelOpen, fetchNotifications, fetchSettings]);

  // Close panel on click outside
  useEffect(() => {
    function handleClickOutside(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        // Don't close if clicking on the bell button
        if (e.target.closest('[data-notification-bell]')) return;
        closePanel();
      }
    }
    if (isPanelOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isPanelOpen, closePanel]);

  // Close on Escape
  useEffect(() => {
    function handleEscape(e) {
      if (e.key === 'Escape') closePanel();
    }
    if (isPanelOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [isPanelOpen, closePanel]);

  const handleNavigate = useCallback((url) => {
    closePanel();
    navigate(url);
  }, [closePanel, navigate]);

  const handleLoadMore = () => {
    if (pagination.page < pagination.totalPages) {
      fetchNotifications(pagination.page + 1, true);
    }
  };

  const filteredNotifications = activeTab === 'all'
    ? notifications
    : notifications.filter((n) => n.category === activeTab);

  if (!isPanelOpen) return null;

  return (
    <>
      {/* Backdrop for mobile */}
      <div className="fixed inset-0 z-40 bg-black/20 sm:hidden" onClick={closePanel} />

      {/* Panel */}
      <div
        ref={panelRef}
        className="fixed sm:absolute right-0 sm:right-0 top-[calc(4rem+env(safe-area-inset-top))] sm:top-16 z-50 w-full sm:w-[420px] max-h-[calc(100vh-5rem-env(safe-area-inset-top))] sm:max-h-[calc(100vh-5rem)] bg-white dark:bg-gray-800 rounded-none sm:rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 flex flex-col overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200"
        style={{ animation: 'slideDown 0.2s ease-out' }}
      >
        {showSettings ? (
          <NotificationSettings
            settings={settings}
            onUpdate={updateSettings}
            onClose={() => setShowSettings(false)}
          />
        ) : (
          <>
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
              <div className="flex items-center gap-2">
                <Bell className="w-5 h-5 text-gray-700 dark:text-gray-300" />
                <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                  Thông báo
                </h2>
                {unreadCount > 0 && (
                  <span className="min-w-[20px] h-5 flex items-center justify-center rounded-full bg-red-500 text-white text-[11px] font-bold px-1.5">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-1">
                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-blue-500 transition-colors"
                    title="Đã đọc tất cả"
                  >
                    <CheckCheck className="w-4.5 h-4.5" />
                  </button>
                )}
                <button
                  onClick={() => setShowSettings(true)}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors"
                  title="Cài đặt thông báo"
                >
                  <Settings className="w-4.5 h-4.5" />
                </button>
                <button
                  onClick={closePanel}
                  className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors sm:hidden"
                >
                  <X className="w-4.5 h-4.5" />
                </button>
              </div>
            </div>

            {/* Category tabs */}
            <div className="flex items-center gap-1 px-3 py-2 border-b border-gray-100 dark:border-gray-700 overflow-x-auto scrollbar-hide">
              {TABS.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`flex-shrink-0 px-3 py-1 text-xs font-medium rounded-full transition-colors ${
                    activeTab === tab.key
                      ? 'bg-primary-100 text-primary-700 dark:bg-primary-900 dark:text-primary-300'
                      : 'text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-700'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Notification list */}
            <div className="flex-1 overflow-y-auto overscroll-contain" style={{ maxHeight: 'calc(100vh - 14rem)' }}>
              {isLoading && notifications.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
                </div>
              ) : filteredNotifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center mb-3">
                    <BellOff className="w-8 h-8 text-gray-300 dark:text-gray-500" />
                  </div>
                  <p className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Không có thông báo
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                    {activeTab === 'all'
                      ? 'Bạn chưa có thông báo nào'
                      : `Không có thông báo ${categoryLabels[activeTab]?.toLowerCase() || ''}`}
                  </p>
                </div>
              ) : (
                <>
                  {filteredNotifications.map((notif) => (
                    <NotificationItem
                      key={notif.notification_id}
                      notification={notif}
                      onRead={markAsRead}
                      onDelete={deleteNotification}
                      onNavigate={handleNavigate}
                    />
                  ))}

                  {/* Load more */}
                  {pagination.page < pagination.totalPages && (
                    <button
                      onClick={handleLoadMore}
                      disabled={isLoading}
                      className="w-full py-3 text-sm text-primary-600 dark:text-primary-400 hover:bg-gray-50 dark:hover:bg-gray-750 flex items-center justify-center gap-1.5 transition-colors"
                    >
                      {isLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <ChevronDown className="w-4 h-4" />
                          Xem thêm
                        </>
                      )}
                    </button>
                  )}
                </>
              )}
            </div>

            {/* Footer */}
            {notifications.length > 0 && (
              <div className="flex items-center justify-between px-4 py-2.5 border-t border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-850">
                <div className="flex items-center gap-2">
                  <button
                    onClick={deleteAllRead}
                    className="text-xs text-gray-400 hover:text-red-500 transition-colors flex items-center gap-1"
                  >
                    <Trash2 className="w-3 h-3" />
                    Xoá đã đọc
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(`Xoá toàn bộ ${pagination.total} thông báo, kể cả chưa đọc? Không khôi phục được.`)) deleteAll();
                    }}
                    className="text-xs text-gray-400 hover:text-red-600 transition-colors flex items-center gap-1"
                    title="Xoá toàn bộ thông báo, kể cả chưa đọc"
                  >
                    <Trash2 className="w-3 h-3" />
                    Xoá tất cả
                  </button>
                </div>
                <span className="text-[10px] text-gray-400">
                  {pagination.total} thông báo
                </span>
                <button
                  onClick={() => { closePanel(); window.location.href = '/notifications'; }}
                  className="text-xs text-primary-600 dark:text-primary-400 hover:underline transition-colors"
                >
                  Xem tất cả →
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        @keyframes slideDown {
          from {
            opacity: 0;
            transform: translateY(-8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </>
  );
}
